import { useEffect, useState } from 'react';
import { api, datum, type Anmelder, type RbAuswahl } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

/**
 * Die Rotberichtigung: aus den Ordnern des Auftrags wird EINE Abgabe.
 *
 * ORIGINALTEXT MARVIN
 *   „Rotberichtigungsunterlagen zusammenstellen, dau Ordner von vorne rein mit
 *    anlegen, und dann wird der ordnerinhalt zusammengestellt."
 *
 * WAS DIESES FENSTER TUT, BEVOR ES ETWAS BAUT: es zeigt, was drin wäre. Jedes
 * Kapitel mit seinem Zustand und, wenn es leer ist, mit dem Grund daneben.
 * Marvin am 07.08.2026 zur Fotodokumentation:
 *
 *   „bei der fotodokumentation sind alle bilder aufgezählt, da wäre es
 *    hilfreich, wenn die ordner noch ständen als aufzählung und da dann der
 *    inhalt angezeigt wird."
 *
 * Beim Gegenprüfen ist genau das die Frage: aus welchem Ordner kommt dieses
 * Foto. Deshalb steht unter jedem Kapitel der Ordner und darunter, was er
 * beisteuert.
 *
 * FEHLT UND ENTFÄLLT SIND ZWEI VERSCHIEDENE DINGE, und sie haben deshalb zwei
 * verschiedene Farben. Ein leeres Tiefbaukapitel bei einem Auftrag ohne
 * Tiefbau ist kein Mangel, sondern die Antwort.
 */

const FARBE: Record<string, string> = {
  da: 'erledigt', fehlt: 'ueberfaellig', entfaellt: 'erledigt',
};
const WORT: Record<string, string> = {
  da: 'liegt vor', fehlt: 'fehlt', entfaellt: 'entfällt',
};

/**
 * ## DERSELBE RUMPF ALS FENSTER UND ALS REITER
 *
 * ORIGINALTEXT MARVIN (18.09.2026)
 *   „ich kann die Rotberichtigung bei der erstellung des LEB nicht öffnen.
 *    oben fehlt der reiter. Die datei soll als neuer Tab geöffnet werden"
 *
 * Bis heute war das hier ausschliesslich ein FENSTER, aufzumachen über das
 * Klappmenü „Vorgang". Wer beim LEB steht und die Rotberichtigung danebenlegen
 * will, musste das Fenster über einem Bildschirm öffnen, den es zudeckt -
 * **ein Fenster über der Sache, die man damit vergleichen will, ist kein
 * Nebeneinander.**
 *
 * `alsReiter` nimmt nur die Hülle weg. Der Rumpf bleibt EINER: zwei Fassungen
 * derselben Karten laufen beim ersten Nachtrag auseinander, und dann zeigt der
 * Reiter etwas anderes als das Fenster.
 */
export default function Rotberichtigung({
  ich, auftragId, zu, fertig, alsReiter,
}: { ich: Anmelder; auftragId: string; zu?: () => void; fertig?: () => void;
     alsReiter?: boolean }) {
  const [d, setD] = useState<any>(null);
  const [art, setArt] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [gebaut, setGebaut] = useState<any>(null);
  /* WELCHER NACHWEIS GERADE GEBAUT WIRD - die Ordnungszahl, oder `alle`.
     Ein einzelner Merker `laeuft` genügt hier nicht: bei acht Zeilen stünde
     sonst an jeder „Wird gebaut …", und niemand wüsste, welche gemeint ist. */
  const [nwLaeuft, setNwLaeuft] = useState<string | null>(null);
  const [nwErgebnis, setNwErgebnis] = useState<any>(null);
  /* ======================================================================
     DIE AUSWAHL VON HAND
     ======================================================================
     ORIGINALTEXT MARVIN (22.09.2026)
       „wenn die RB manuell erstellt wird muss ich die möglichkeiten haben,
        die ordner/dateien selbst noch zu selektieren, falls das system was
        nicht richtig erkannt hat."

     SIE LEBT HIER UND NICHT IM AUFTRAG. Eine Abgabe wird in EINEM Zug
     zusammengestellt; eine gespeicherte Auswahl wäre eine zweite Wahrheit
     neben der Ablage und beim nächsten Lauf mit anderen Dateien falsch.
     **Was man einmal entscheidet, muss man nicht aufheben.**

     SIE STEHT ALS WUNSCH DA UND NICHT ALS ERGEBNIS. Was wirklich hineinkommt,
     sagt der Server in `d.kapitel[].stuecke[].mit` - dieselben 2 Listen gehen
     hin und kommen angewandt zurück. */
  const [ohne, setOhne] = useState<string[]>([]);
  const [dazu, setDazu] = useState<{ bezugId: string; kapitel: string }[]>([]);
  const [weitereAuf, setWeitereAuf] = useState(false);

  function auswahl(
    o: string[] = ohne,
    z: { bezugId: string; kapitel: string }[] = dazu,
  ): RbAuswahl | undefined {
    return o.length || z.length ? { ohne: o, dazu: z } : undefined;
  }

  /* DIE LISTEN WERDEN ÜBERGEBEN UND NICHT AUS DEM ZUSTAND GELESEN.
     `setOhne` wirkt erst im nächsten Durchlauf; wer hier `ohne` läse, schickte
     den Stand VOR dem Klick los - und der Bildschirm hinkte bei jedem Haken
     um einen hinterher. */
  async function laden(
    a?: string | null,
    o: string[] = ohne,
    z: { bezugId: string; kapitel: string }[] = dazu,
  ) {
    try {
      const r = await api.rb(auftragId, a ?? undefined, auswahl(o, z));
      setD(r);
      setArt(r.gewaehlt);
      setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [auftragId]);
  useFrisch(laden);

  /** Den Haken an einer Datei umlegen. */
  async function hakenUm(bezugId: string, mit: boolean) {
    const o = mit ? ohne.filter((x) => x !== bezugId) : [...ohne, bezugId];
    /* WER EINE DAZUGENOMMENE DATEI WIEDER ABWÄHLT, NIMMT SIE GANZ HERAUS.
       Sie mit einem `ohne` zu überschreiben ginge auch, hinterliesse aber 2
       Einträge über dieselbe Datei - und die Liste unten zeigte sie dann
       weder hier noch dort. */
    const z = mit ? dazu : dazu.filter((x) => x.bezugId !== bezugId);
    setOhne(o); setDazu(z);
    await laden(art, o, z);
  }

  /** Viele Haken auf einmal (.287): eine ganze Gruppe oder ein ganzes Kapitel.
      ORIGINALTEXT MARVIN (02.10.2026): „hier muss ich die gruppen
      selektieren/deselektieren können, so dauert das zu lange alle einzeln
      anzuklicken." */
  async function hakenAlle(bezugIds: string[], mit: boolean) {
    const ids = new Set(bezugIds);
    const o = mit ? ohne.filter((x) => !ids.has(x))
                  : [...ohne.filter((x) => !ids.has(x)), ...bezugIds];
    const z = mit ? dazu : dazu.filter((x) => !ids.has(x.bezugId));
    setOhne(o); setDazu(z);
    await laden(art, o, z);
  }

  /** Eine übrige Datei einem Kapitel zuschlagen - oder wieder herausnehmen. */
  async function kapitelZu(bezugId: string, kapitel: string) {
    const z = kapitel
      ? [...dazu.filter((x) => x.bezugId !== bezugId), { bezugId, kapitel }]
      : dazu.filter((x) => x.bezugId !== bezugId);
    const o = ohne.filter((x) => x !== bezugId);
    setDazu(z); setOhne(o);
    await laden(art, o, z);
  }

  /* DIE HÜLLE. Als Reiter ist sie leer - der Bildschirm hat seinen Titel
     schon oben in der Akte, und ein zweiter darunter wäre eine Überschrift
     über einer Überschrift. */
  const Huelle = ({ children }: { children: any }) => (
    alsReiter
      ? <div className="rbreiter">{children}</div>
      : (
        <Dialog
          titel="Rotberichtigung zusammenstellen"
          untertitel={d ? d.auftrag.adresse + ', KLS ' + (d.auftrag.kls_id ?? '?')
                        : undefined}
          breite={980} laeuft={laeuft} zu={zu ?? (() => {})}
        >
          {children}
        </Dialog>
      )
  );

  if (!d) {
    return <Huelle><p className="klein leise">Wird geladen.</p></Huelle>;
  }

  const fehlend = (d.kapitel ?? []).filter((k: any) => k.zustand === 'fehlt');
  const leer = d.dateien === 0;
  /* WIE VIELE NACHWEISE NOCH GAR NICHT IM ORDNER LIEGEN. Sie steht im Kopf
     der Karte, damit man es sieht, ohne die Zeilen zu lesen - und nur dann,
     wenn sie etwas sagt. */
  const nachweiseOffen = (d.nachweise ?? []).filter((n: any) => !n.datei).length;

  async function waehle(neu: string) {
    setArt(neu);
    setGebaut(null);
    /* DIE AUSWAHL FÄLLT MIT DER VARIANTE.
       Ein Schlussaufmaß hat ANDERE Kapitel als ein komplettes: „lageplan"
       gibt es dort nicht. Eine mitgeschleppte Zuordnung zeigte auf ein
       Kapitel, das es in der neuen Variante nicht gibt - und der Server
       wiese sie zu Recht ab. **Wer die Frage wechselt, fängt mit der Antwort
       von vorn.** */
    setOhne([]); setDazu([]);
    await laden(neu, [], []);
  }

  /**
   * Die ausdrückliche Wahl festhalten.
   *
   * Sie ist der einzige Weg, auf dem ein Teilaufmaß entsteht: von allein wird
   * es nie vorgeschlagen, sonst hätte jeder laufende Auftrag ein
   * unvollständiges Paket.
   */
  async function merken() {
    setLaeuft(true);
    try {
      await api.rbArt(auftragId, art === d.vorschlag.art ? null : art);
      await laden(art);
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /**
   * DIE NACHWEISE VON HAND ERZEUGEN - alle, oder genau einen.
   *
   * ORIGINALTEXT MARVIN (14.09.2026, Bild 7 der offenen Liste)
   *   „Nachweis von Hand erzeugen"
   *
   * **ER BAUT DIESELBEN DATEIEN WIE DAS ZUSAMMENSTELLEN DER ABGABE.** Der Weg
   * dahinter ruft dieselbe Funktion; zwei Fassungen liefen beim ersten
   * Nachtrag auseinander, und dann heftet der eine Weg ein HEIC mit und der
   * andere nicht.
   *
   * DANACH WIRD NEU GELADEN, und zwar mit `art`: die Vorschau sagt, was
   * WIRKLICH im Ordner liegt, und ohne das Nachladen stünde neben der eben
   * gebauten Zeile weiter „fehlt". Der Stand von vorher ist für den, der
   * davorsitzt, von einem gescheiterten Lauf nicht zu unterscheiden.
   *
   * `fertig()` wird hier NICHT gerufen: das meldet der Akte, dass eine ABGABE
   * entstanden ist, und das ist hier nicht geschehen.
   */
  async function nachweiseBauen(oz: string | null) {
    setNwLaeuft(oz ?? 'alle');
    setFehler(null);
    setNwErgebnis(null);
    try {
      const r = await api.rbNachweise(auftragId, oz);
      setNwErgebnis(r);
      await laden(art);
    } catch (f) { setFehler(ausFehler(f)); } finally { setNwLaeuft(null); }
  }

  async function bauen() {
    setLaeuft(true);
    setFehler(null);
    try {
      /* DIESELBE AUSWAHL, DIE IN DER VORSCHAU STEHT. Wer sie hier weggliesse,
         baute etwas anderes, als der Bildschirm eben gezeigt hat - der eine
         Fehler, den man an einer fertigen PDF nicht mehr sieht. */
      const r = await api.rbBauen(auftragId, art!, auswahl());
      setGebaut(r);
      await laden(art);
      fertig?.();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Huelle>
      <Fehlerkasten lage={fehler} />

      {/* Die Variante. Der Vorschlag steht daneben, mit seiner Begründung:
          ein Statuswort ist keine Antwort. */}
      <div className="karte">
        <h3>Welche Abgabe</h3>
        <div className="leib">
          <div className="reihe" style={{ flexWrap: 'wrap', gap: 8 }}>
            {(d.arten ?? []).map((a: any) => (
              <button
                key={a.wert}
                className={'knopf' + (a.wert === art ? ' haupt' : '')}
                disabled={laeuft}
                onClick={() => waehle(a.wert)}
              >
                {a.text}
                {a.wert === d.vorschlag.art && ' ✓'}
              </button>
            ))}
            <div className="wachsen" />
            {art !== d.vorschlag.art && (
              <button className="knopf" disabled={laeuft} onClick={merken}>
                Diese Wahl festhalten
              </button>
            )}
          </div>

          <p className="klein" style={{ marginBottom: 0 }}>
            <b>Vorschlag: {d.arten.find((a: any) => a.wert === d.vorschlag.art)?.text}.</b>
            {' '}{d.vorschlag.grund}
          </p>

          {d.gewuenscht && (
            <p className="klein leise" style={{ marginBottom: 0 }}>
              Für diesen Auftrag ist
              {' '}<b>{d.arten.find((a: any) => a.wert === d.gewuenscht)?.text}</b>
              {' '}festgehalten. Das schlägt jeden Vorschlag.
            </p>
          )}

          {art === 'teil' && (
            <div className="hinweis" style={{ marginBottom: 0 }}>
              Ein Teilaufmaß ist nur dann richtig, wenn die Montage <b>nicht
              mehr kommt</b> (Einblasstop). Sonst warten Sie auf sie: Teil- und
              Schlussaufmaß sind 2 Abgaben und 2 LEB-Dateien.
            </div>
          )}
        </div>
      </div>

      {/* Die Kapitel, in der Reihenfolge, in der sie in der Datei stehen. */}
      <div className="karte">
        <h3>
          Was hineinkommt
          <span className="weg">
            {d.dateien} Unterlage{d.dateien === 1 ? '' : 'n'}
            {fehlend.length > 0 && `, ${fehlend.length} Kapitel offen`}
          </span>
        </h3>
        <div className="leib">
          <table className="liste">
            <thead>
              <tr>
                <th style={{ width: 34 }}>Nr.</th>
                <th>Kapitel</th>
                <th style={{ width: 110 }}>Zustand</th>
              </tr>
            </thead>
            <tbody>
              {(d.kapitel ?? []).map((k: any, i: number) => (
                <tr key={k.id} style={{ cursor: 'default' }}>
                  <td className="klein leise">{i + 1}</td>
                  <td>
                    <b>{k.titel}</b>
                    {/* ==========================================================
                        JEDE DATEI EINZELN, MIT HAKEN UND MIT WEG DORTHIN.
                        ==========================================================
                        ORIGINALTEXT MARVIN (22.09.2026)
                          „wenn die RB manuell erstellt wird muss ich die
                           möglichkeiten haben, die ordner/dateien selbst noch zu
                           selektieren, falls das system was nicht richtig erkannt
                           hat."
                          „ausserdem sollen die pdfs immer im neuen tab geöffnet
                           werden, damit ich weiterarbeiten kann und das parallel
                           öffnen."

                        Hier stand bis heute eine Zeile je Ordner mit den Namen als
                        TEXT. **Ein Dateiname, den man nicht aufmachen kann, ist
                        keine Auskunft, sondern eine Behauptung:** wer prüfen will,
                        ob wirklich der rote Lageplan gemeint ist, musste in den
                        Dateireiter wechseln und dort suchen.

                        `target="_blank"` UND KEIN VORSCHAUFENSTER. Das eingebaute
                        Fenster legt sich über genau die Liste, gegen die verglichen
                        wird - und dann ist es kein Nebeneinander mehr. Ein eigener
                        Reiter lässt sich auf den zweiten Bildschirm ziehen.

                        DER ORDNER STEHT DAVOR. Marvin am 07.08.2026: „da wäre es
                        hilfreich, wenn die ordner noch ständen als aufzählung und
                        da dann der inhalt angezeigt wird." Beim Gegenprüfen ist das
                        die Frage - aus welchem Ordner kommt dieses Foto. */}
                    {(k.stuecke ?? []).length > 1 && (
                      <div className="klein rbgruppen">
                        <button type="button" className="knopf klein" disabled={laeuft}
                                onClick={() => void hakenAlle(k.stuecke.map((x: any) => x.bezugId), true)}>alle an</button>
                        <button type="button" className="knopf klein" disabled={laeuft}
                                onClick={() => void hakenAlle(k.stuecke.map((x: any) => x.bezugId), false)}>alle ab</button>
                        {gruppenVon(k.stuecke).filter((g) => g.stuecke.length > 1).map((g) => {
                          const an = g.stuecke.filter((x: any) => x.mit).length;
                          return (
                            <label key={g.ordner} className="rbgruppe" title={`Alle Dateien aus „${g.ordner}" an- oder abwählen`}>
                              <input type="checkbox" disabled={laeuft}
                                     checked={an === g.stuecke.length}
                                     ref={(el) => { if (el) el.indeterminate = an > 0 && an < g.stuecke.length; }}
                                     onChange={() => void hakenAlle(g.stuecke.map((x: any) => x.bezugId), an < g.stuecke.length)} />
                              {' '}{g.ordner} <span className="leise">({an}/{g.stuecke.length})</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {(k.stuecke ?? []).map((s: any) => (
                      <div key={s.bezugId} className="klein">
                        <label style={{ cursor: 'pointer' }}>
                          <input type="checkbox" checked={s.mit}
                                 disabled={laeuft}
                                 onChange={() => void hakenUm(s.bezugId, !s.mit)} />
                          {' '}
                          <span className="leise">{s.ordner}: </span>
                          <a href={api.dateiAdresse(s.bezugId, s.dateiname)}
                             target="_blank" rel="noreferrer"
                             onClick={(e) => e.stopPropagation()}
                             style={s.mit ? undefined
                                          : { textDecoration: 'line-through' }}>
                            {s.dateiname}
                          </a>
                        </label>
                      </div>
                    ))}
                    {k.hinweis && (
                      <div className={'klein ' + (k.zustand === 'fehlt' ? '' : 'leise')}>
                        {k.hinweis}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={'marke-zustand z-' + FARBE[k.zustand]}>
                      {WORT[k.zustand]}
                      {k.zustand === 'da' && ` (${k.anzahl})`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {fehlend.length > 0 && (
            <div className="hinweis warnung" style={{ marginBottom: 0 }}>
              {fehlend.length === 1 ? 'Ein Kapitel bleibt' : `${fehlend.length} Kapitel bleiben`}
              {' '}leer: {fehlend.map((k: any) => k.titel).join(', ')}. Die Abgabe
              lässt sich trotzdem bauen, sie ist dann aber unvollständig.
            </div>
          )}

          {/* ==============================================================
              WAS IM ORDNER LIEGT UND IN KEIN KAPITEL GEFUNDEN HAT.
              ==============================================================
              **Der Fall, den Marvin meint, steht genau hier.** Ein Lageplan,
              den jemand in „Montage/05 Sonstiges" abgelegt hat, gehört in die
              Abgabe und kommt nie hinein: die Erkennung geht über den ORDNER.
              Ohne diese Liste sähe man nicht einmal, dass es ihn gibt - eine
              Vorschau zeigt sonst nur, was sie selbst gefunden hat, und das
              ist die eine Sicht, in der ein Fehler der Erkennung unsichtbar
              bleibt.

              ZUGEKLAPPT, SOLANGE NIEMAND FRAGT. In einem Auftrag mit 80
              Fotos sind das 80 Zeilen, und sie beantworten für die meisten
              Abgaben keine Frage. Die ZAHL steht am Knopf: eine Klappe, die
              nicht sagt, was dahinter liegt, macht niemand auf. */}
          {(d.weitere ?? []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button type="button" className="knopf"
                      onClick={() => setWeitereAuf(!weitereAuf)}>
                {weitereAuf ? 'Weitere Dateien zuklappen' : 'Weitere Dateien im Auftragsordner'}
                <span className="merker">{d.weitere.length}</span>
              </button>
              {weitereAuf && (
                <div className="leib" style={{ paddingLeft: 0 }}>
                  <p className="klein leise">
                    Diese Dateien liegen am Auftrag und stehen in keinem
                    Kapitel. Wählen Sie rechts das Kapitel, in das eine
                    gehört - sie wird dann ans Ende dieses Kapitels geheftet.
                  </p>
                  <table className="liste">
                    <tbody>
                      {d.weitere.map((s: any) => (
                        <tr key={s.bezugId} style={{ cursor: 'default' }}>
                          <td className="klein">
                            <span className="leise">{s.ordner}: </span>
                            <a href={api.dateiAdresse(s.bezugId, s.dateiname)}
                               target="_blank" rel="noreferrer">{s.dateiname}</a>
                          </td>
                          <td style={{ width: 280 }}>
                            <select className="feld" disabled={laeuft}
                                    value={dazu.find((x) => x.bezugId === s.bezugId)?.kapitel ?? ''}
                                    onChange={(e) => void kapitelZu(s.bezugId, e.target.value)}>
                              <option value="">nicht mitnehmen</option>
                              {(d.kapitelWahl ?? []).map((k: any) => (
                                <option key={k.id} value={k.id}>{k.titel}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* WAS VON HAND GEÄNDERT WURDE, STEHT DA. Eine Abgabe, die anders
              aussieht als die Erkennung sie gebaut hätte, muss das sagen -
              sonst drückt jemand „zusammenstellen" und weiss nicht mehr, was
              er 10 Minuten vorher abgewählt hat. */}
          {(d.auswahl?.weggelassen > 0 || d.auswahl?.dazugenommen > 0) && (
            <div className="hinweis" style={{ marginBottom: 0 }}>
              Von Hand: <b>{d.auswahl.weggelassen}</b> abgewählt,
              {' '}<b>{d.auswahl.dazugenommen}</b> dazugenommen. Genau so wird
              die Mappe gebaut, und es steht hinterher in der Auftragsinfo.
              {' '}
              <button type="button" className="knopf" disabled={laeuft}
                      onClick={() => { setOhne([]); setDazu([]); void laden(art, [], []); }}>
                Auswahl zurücksetzen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ==================================================================
          DIE NACHWEISE, BEVOR SIE ENTSTEHEN.
          ==================================================================
          Sie kommen NICHT in die Mappe, sie entstehen daneben - eine eigene
          PDF je nachweispflichtiger Ordnungszahl, für die eVergabe. Bis zum
          09.09.2026 erfuhr man erst NACH dem Bauen, dass es sie gibt; sie
          stehen deshalb in einer eigenen Karte und nicht als Zeile in der
          Kapitelliste, denn dort läse man sie als Teil der Datei.

          DIE ZAHL KOMMT VOM SERVER. Er rechnet sie mit derselben Regel aus,
          mit der er sie hinterher baut; wer sie hier aus den Ordnernamen
          nachbaute, hätte eine zweite Wahrheit über dieselbe Frage.

          UND SEIT DEM 14.09.2026 STEHT NEBEN JEDER ZEILE EIN KNOPF.

          ORIGINALTEXT MARVIN (14.09.2026, Bild 7 der offenen Liste)
            „Nachweis von Hand erzeugen"

          Bis dahin entstand ein Nachweis ausschliesslich als NEBENWIRKUNG des
          Zusammenstellens der ganzen Abgabe: wer EINEN Ordner nachträglich
          gefüllt hat, musste die komplette Mappe neu bauen - und die trägt
          danach ein neues Datum im Namen und ersetzt die vorige.

          DIE KARTE ERSCHEINT AUCH DANN, WENN ES NICHTS ZU BAUEN GIBT und
          etwas VERWAISTES im Ordner liegt. Ein Nachweis, dessen Ordner leer
          geräumt wurde, geht nicht von selbst weg (er kann längst bei der
          eVergabe liegen) - und wenn ihn diese Karte nicht nennt, nennt ihn
          niemand. */}
      {/* UND SIE STEHT JETZT IMMER DA.
          ORIGINALTEXT MARVIN (18.09.2026)
            „mir fehlt der knopf in dem bereich „Rotberichtigung" das ich den
             nachweis dort anstoßen kann."
          Hier hing die ganze Karte an `nachweise.length > 0` - also daran,
          dass schon ein Ordner mit Bildern dasteht. Wer nachsehen wollte, ob
          ein Nachweis fehlt, sah GAR NICHTS, und das ist von „es gibt hier
          nichts zu tun" nicht zu unterscheiden. **Eine Karte, die nur
          erscheint, wenn schon alles da ist, beantwortet die Frage nie, für
          die man sie aufschlägt.** */}
      {true && (
        <div className="karte">
          <h3>
            Eigene Nachweise für eVergabe
            <span className="weg">
              {(d.nachweise ?? []).length} PDF
              {nachweiseOffen > 0 && `, ${nachweiseOffen} noch nicht erzeugt`}
            </span>
            {/* DER SAMMELKNOPF STEHT IM KOPF UND NICHT UNTER DER TABELLE.
                Bei 8 Positionen liegt er sonst ausserhalb des Bildes, und ein
                Knopf, den man erst suchen muss, ist der Knopf, den Marvin
                vermisst hat. */}
            {(d.nachweise ?? []).length > 0 && (
              <button className="knopf klein"
                      style={{ float: 'right', fontWeight: 400 }}
                      disabled={laeuft || nwLaeuft !== null}
                      onClick={() => nachweiseBauen(null)}>
                {nwLaeuft === 'alle' ? 'Wird gebaut …' : 'Alle erzeugen'}
              </button>
            )}
          </h3>
          <div className="leib">
            {/* WAS ZU TUN IST, WENN NOCH NICHTS DA IST. */}
            {(d.nachweise ?? []).length === 0
              && (d.nachweiseVerwaist ?? []).length === 0 && (
              <p className="klein leise" style={{ marginTop: 0 }}>
                Zu diesem Auftrag liegt noch kein Nachweisordner mit Bildern.
                Ein Nachweisordner entsteht an einer Aufmaßzeile, deren
                Position im Leistungskatalog unter „Nachweis" einen Eintrag
                trägt; die PDF entsteht daraus, sobald Bilder darin liegen.
              </p>
            )}
            {(d.nachweise ?? []).length > 0 && (
              <>
                <table className="liste">
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>Position</th>
                      <th>Aus diesen Ordnern</th>
                      <th style={{ width: 70 }}>Bilder</th>
                      <th style={{ width: 150 }}>Nachweis</th>
                      <th style={{ width: 130 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.nachweise.map((n: any) => (
                      <tr key={n.oz} style={{ cursor: 'default' }}>
                        <td><b>{n.oz}</b></td>
                        <td>
                          <div className="klein leise">
                            {(n.ordner ?? []).join(', ')}
                          </div>
                          <div className="klein leise">{n.dateiname}</div>
                        </td>
                        <td>{n.anzahl}</td>
                        {/* LIEGT ER DA ODER NICHT - und seit wann.
                            „4 Bilder" allein sagt nur, was entstünde. */}
                        <td>
                          {n.datei ? (
                            <>
                              {/* AUCH HIER: WAS DALIEGT, LÄSST SICH AUFMACHEN.
                                  Neuer Tab, damit die Vorschau stehenbleibt -
                                  man vergleicht ein Nachweispapier mit dem,
                                  was danebensteht. */}
                              <a className="knopfverweis"
                                 href={api.dateiAdresse(n.datei.bezugId,
                                                        n.datei.dateiname)}
                                 target="_blank" rel="noreferrer">
                                <span className="marke-zustand z-erledigt">
                                  liegt vor
                                </span>
                              </a>
                              <div className="klein leise">
                                {datum(n.datei.erstellt_am)}
                              </div>
                            </>
                          ) : (
                            <span className="marke-zustand z-ueberfaellig">
                              fehlt
                            </span>
                          )}
                        </td>
                        <td className="knopfleiste">
                          <button
                            className="knopf"
                            disabled={laeuft || nwLaeuft !== null}
                            onClick={() => nachweiseBauen(n.oz)}
                          >
                            {nwLaeuft === n.oz
                              ? 'Wird gebaut …'
                              : n.datei ? 'Neu erzeugen' : 'Erzeugen'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="reihe" style={{ marginTop: 8 }}>
                  <div className="wachsen" />
                  <button
                    className="knopf"
                    disabled={laeuft || nwLaeuft !== null}
                    onClick={() => nachweiseBauen(null)}
                  >
                    {nwLaeuft === 'alle'
                      ? 'Wird gebaut …'
                      : 'Alle Nachweise erzeugen'}
                  </button>
                </div>
              </>
            )}

            {/* WAS IM ORDNER LIEGT UND ZU KEINER GRUPPE MEHR GEHÖRT.
                Es wird GENANNT und nicht weggeräumt: die Datei kann längst
                bei der eVergabe liegen, und ein stiller Verlust wäre der
                teurere Ausgang. Wer sie loswerden will, nimmt sie im
                Dateireiter weg - dort steht, was mit ihr geschieht. */}
            {(d.nachweiseVerwaist ?? []).length > 0 && (
              <div className="hinweis warnung">
                <b>
                  {d.nachweiseVerwaist.length === 1
                    ? 'Ein Nachweis liegt im Ordner'
                    : `${d.nachweiseVerwaist.length} Nachweise liegen im Ordner`}
                  , zu dem es nichts mehr zu bauen gibt.
                </b>
                <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                  {d.nachweiseVerwaist.map((v: any) => (
                    <li key={v.oz} className="klein">
                      Position {v.oz}: {v.dateiname}, {datum(v.erstellt_am)}
                    </li>
                  ))}
                </ul>
                <div className="klein" style={{ marginTop: 4 }}>
                  Entweder ist der Nachweisordner leer geräumt, oder die
                  Position verlangt keinen Nachweis mehr. Die Datei bleibt
                  liegen; wer sie wegnehmen will, tut es im Reiter „Dateien".
                </div>
              </div>
            )}

            {/* WAS DER LETZTE HANDGRIFF WIRKLICH GETAN HAT. */}
            {nwErgebnis && (
              <div className="hinweis erledigt">
                <b>
                  {nwErgebnis.nachweise.length === 1
                    ? 'Ein Nachweis steht im Auftragsordner'
                    : `${nwErgebnis.nachweise.length} Nachweise stehen im Auftragsordner`}
                  :
                </b>
                <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                  {nwErgebnis.nachweise.map((n: any) => (
                    <li key={n.oz} className="klein">
                      {n.dateiname}, {n.seiten} Seite
                      {n.seiten === 1 ? '' : 'n'}
                      {n.ersetzt && `, ersetzt ${n.ersetzt}`}
                    </li>
                  ))}
                </ul>
                {(nwErgebnis.ausgefallen ?? []).length > 0 && (
                  <div className="klein" style={{ marginTop: 4 }}>
                    Nicht übernommen: {nwErgebnis.ausgefallen
                      .map((a: any) => `${a.dateiname} (${a.grund})`).join(' ')}
                  </div>
                )}
                {(nwErgebnis.hinweise ?? []).length > 0 && (
                  <div className="klein" style={{ marginTop: 4 }}>
                    {nwErgebnis.hinweise.join(' ')}
                  </div>
                )}
              </div>
            )}

            <div className="hinweis">
              <b>Sie gehen zusätzlich hinaus, nicht statt dessen.</b> Dieselben
              Bilder bleiben in der Fotodokumentation der Abgabe: die Telekom
              bekommt die Mappe, die eVergabe den Nachweis zur Position. Ein
              neuer Lauf ersetzt die vorigen Nachweise, statt eine zweite
              Datei mit demselben Namen daneben zu legen.
            </div>
          </div>
        </div>
      )}

      {/* Was schon gebaut wurde. Gemerkt, nicht gesucht. */}
      {(d.frueher ?? []).length > 0 && (
        <div className="karte">
          <h3>Schon erzeugt</h3>
          <div className="leib">
            <table className="liste">
              <tbody>
                {d.frueher.map((f: any) => (
                  <tr key={f.id} style={{ cursor: 'default' }}>
                    <td>
                      {/* DIE DATEI GEHT IN EINEM NEUEN TAB AUF.
                          „Die datei soll als neuer Tab geöffnet werden" - hier
                          stand `<b>{f.dateiname}</b>`, also der Name als
                          fetter Text. Ein Papier, das dasteht und sich nicht
                          aufmachen lässt, schickt den Leser in die Dateiliste
                          und dort auf die Suche.
                          `bezug_id` kann fehlen, wenn jemand die Datei
                          inzwischen entfernt hat; dann bleibt der Name stehen
                          und gibt kein Versprechen. */}
                      {f.bezug_id ? (
                        <a className="knopfverweis"
                           href={api.dateiAdresse(f.bezug_id, f.dateiname)}
                           target="_blank" rel="noreferrer">
                          <b>{f.dateiname}</b>
                        </a>
                      ) : <b>{f.dateiname}</b>}
                      <div className="klein leise">
                        {f.seiten} Seite{f.seiten === 1 ? '' : 'n'} aus {f.teile}
                        {' '}Unterlagen, {datum(f.erstellt_am)}
                        {f.erstellt_von && `, ${f.erstellt_von}`}
                      </div>
                      {/* FEHLT IST NICHT ENTFÄLLT, und beides steht jetzt hier.
                          Gezählt hat es `v_rotberichtigung` seit Migration 019,
                          gelesen hat die Sicht niemand. Ein leeres
                          Tiefbaukapitel bei einem Auftrag ohne Tiefbau ist kein
                          Mangel, sondern die Antwort; ein leeres bei einem
                          Auftrag MIT Tiefbau ist eine Abgabe, die die Telekom
                          zurückschickt. */}
                      {(f.kapitel_fehlend > 0 || f.kapitel_entfaellt > 0) && (
                        <div className="klein">
                          {f.kapitel_fehlend > 0 && (
                            <span style={{ color: 'var(--ueberfaellig)' }}>
                              {f.kapitel_fehlend} Kapitel fehlt
                              {f.kapitel_fehlend === 1 ? '' : 'en'}
                            </span>
                          )}
                          {f.kapitel_fehlend > 0 && f.kapitel_entfaellt > 0 && ', '}
                          {f.kapitel_entfaellt > 0 && (
                            <span className="leise">
                              {f.kapitel_entfaellt} entfäll
                              {f.kapitel_entfaellt === 1 ? 't' : 'en'}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ width: 110 }}>
                      {f.ersetzt_am
                        ? <span className="marke-zustand z-neu">ersetzt</span>
                        : <span className="marke-zustand z-erledigt">gültig</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="klein leise" style={{ marginBottom: 0 }}>
              Die Dateien liegen im Auftragsordner und sind unter „Dateien" zu
              öffnen. Ein zweiter Lauf ersetzt den vorigen, er stellt sich nicht
              daneben.
            </p>
          </div>
        </div>
      )}

      {gebaut && (
        <div className="hinweis erledigt">
          <b>{gebaut.dateiname}</b> steht im Auftragsordner: {gebaut.seiten}
          {' '}Seite{gebaut.seiten === 1 ? '' : 'n'} aus {gebaut.teile} Unterlagen.
          {(gebaut.nachweise ?? []).length > 0 && (
            <div className="klein">
              Dazu {gebaut.nachweise.length} Einzelnachweis
              {gebaut.nachweise.length === 1 ? '' : 'e'} für eVergabe:
              {' '}{gebaut.nachweise.map((n: any) => n.dateiname).join(', ')}.
            </div>
          )}
          {/* UND WAS DER AUFTRAG JETZT IST. Dieselbe Auskunft wie beim LEB:
              der Server entscheidet nach jeder gebauten Mappe, ob der
              Auftrag abgeschlossen ist, und nennt den Grund, wenn nicht.
              **Eine Automatik, die stumm entscheidet, ist von einer
              fehlenden Automatik nicht zu unterscheiden.** */}
          {gebaut.abschluss && (
            <div className="klein" style={{ marginTop: 4 }}>
              {gebaut.abschluss.setzen
                ? <b>Der Auftrag steht jetzt auf „erledigt".</b>
                : <>Der Auftrag bleibt offen: {gebaut.abschluss.grund}</>}
            </div>
          )}
          {(gebaut.ausgefallen ?? []).length > 0 && (
            <div className="klein">
              Nicht übernommen: {gebaut.ausgefallen
                .map((a: any) => `${a.dateiname} (${a.grund})`).join(' ')}
            </div>
          )}
          {/* WAS AUS DEN FORMULAREN GEWORDEN IST - GEZÄHLT AN DIESER MAPPE.
              ============================================================
              ORIGINALTEXT MARVIN (15.09.2026)
                „hast du den fehler behoben, dass die einzelpdf 1:1 in die
                 sammelpdf übernommen werden und dort keine felder,
                 checkboxen, haken verloren gehen?"

              **Die Frage ist nicht mit dem Verfahren zu beantworten,
              sondern nur mit der Mappe.** Prüfstand 147 beweist, dass der
              Weg richtig ist - an Stücken, die er sich selbst baut. Ob
              SEINE Checkliste heil durchkommt, steht in SEINER Mappe.

              Der Satz steht NUR da, wenn wirklich ein Formular dabei war
              (`formular !== null`). Eine Mappe aus lauter Fotos kann keine
              Kästchen verlieren, und eine Zeile, die bei jeder zweiten
              Abgabe „0 Felder" sagt, wird beim dritten Mal überlesen. */}
          {gebaut.formular && (
            <div className="klein">
              {gebaut.formular.felder} Formularfeld
              {gebaut.formular.felder === 1 ? '' : 'er'} übernommen
              {gebaut.formular.gefuelltVorher > 0 && (
                <>, davon {gebaut.formular.gefuelltNachher} von{' '}
                {gebaut.formular.gefuelltVorher} ausgefüllten Feldern</>
              )}.
            </div>
          )}
        </div>
      )}

      {/* WAS BEIM HEFTEN AUFFIEL, STEHT IN EINEM EIGENEN KASTEN.
          ==================================================================
          Der Kasten darüber ist grün und sagt „fertig". Ein Rückfall auf
          `pdfunite` kostet die Kreuze in einer ausgefüllten Checkliste - das
          ist keine Fussnote zu einer guten Nachricht, sondern eine eigene.

          **Er steht nur da, wenn es etwas zu sagen gibt.** Ein Kasten, der
          bei jedem Lauf dasteht, wird beim dritten Mal überlesen, und dann
          auch der, der zutrifft. */}
      {gebaut && (gebaut.hinweise ?? []).length > 0 && (
        <div className="hinweis warnung">
          <b>Beim Heften ist etwas aufgefallen</b>
          <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
            {gebaut.hinweise.map((h: string, i: number) => (
              <li key={i} className="klein">{h}</li>
            ))}
          </ul>
          <div className="klein" style={{ marginTop: 4 }}>
            Die Mappe ist entstanden und hat ihre Seiten. Sehen Sie die
            ausgefüllten Vordrucke darin nach, bevor die Abgabe hochgeht.
          </div>
        </div>
      )}

      <div className="reihe" style={{ marginTop: 10 }}>
        <div className="wachsen" />
        <button
          className="knopf haupt"
          disabled={laeuft || leer}
          onClick={bauen}
          title={leer
            ? 'Für diese Abgabe liegt keine einzige Datei vor.'
            : undefined}
        >
          {laeuft ? 'Wird gebaut …' : 'Zusammenstellen'}
        </button>
      </div>

      {leer && (
        <p className="klein" style={{ textAlign: 'right', marginBottom: 0 }}>
          Es liegt keine einzige Unterlage vor. Ohne Inhalt entstünde eine leere
          PDF, und die sieht beim Empfänger aus wie ein Fehler.
        </p>
      )}
    </Huelle>
  );
}

/** Die Dateien eines Kapitels nach ihrem Ordner, in der Reihenfolge des Kapitels. */
function gruppenVon(stuecke: any[]): { ordner: string; stuecke: any[] }[] {
  const raus: { ordner: string; stuecke: any[] }[] = [];
  for (const s of stuecke ?? []) {
    const o = String(s.ordner ?? '');
    const g = raus.find((x) => x.ordner === o);
    if (g) g.stuecke.push(s); else raus.push({ ordner: o, stuecke: [s] });
  }
  return raus;
}
